import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import BackMethods from "../../back_component/BackMethods";
import { PATHS } from "../../routes/paths";
import { FaArrowRight, FaTags } from "react-icons/fa";
import { can, PERMISSIONS } from "../../auth/permissions";
import "../../styles/admin-shared.css";
import "../Users/users-admin.css";
import ComplaintCategoriesModals from "./ComplaintCategoriesModals";

export default function ComplaintCategoriesPage() {
  const { backEnd_request } = BackMethods();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [flash, setFlash] = useState(null);

  const [modal, setModal] = useState(null);
  const [modalCategoryId, setModalCategoryId] = useState(null);
  const [modalCategoryInitial, setModalCategoryInitial] = useState(null);

  const canCreate = can(PERMISSIONS.COMPLAINT_CATEGORIES_CREATE);
  const canEdit = can(PERMISSIONS.COMPLAINT_CATEGORIES_EDIT);
  const hasAnyAction = canEdit;

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await backEnd_request.get("/ComplaintCategories?includeInactive=true");
      setCategories(response.data || []);
    } catch (err) {
      console.error("Error loading complaint categories:", err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [backEnd_request]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (location.state?.successMessage) {
      setFlash({ message: location.state.successMessage, variant: "success" });
      navigate(".", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (!flash?.message) return undefined;
    const t = setTimeout(() => setFlash(null), 4500);
    return () => clearTimeout(t);
  }, [flash]);

  const filteredCategories = categories
    .filter((item) => {
      const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "all"
          ? true
          : statusFilter === "active"
            ? item.isActive === true
            : item.isActive === false;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

  const handleToggleStatus = async (item) => {
    if (!canEdit) return;
    const nextActive = !item.isActive;
    const label = nextActive ? "تفعيل" : "إيقاف";
    if (!window.confirm(`هل أنت متأكد من ${label} هذا التصنيف؟`)) return;
    try {
      await backEnd_request.post(`/ComplaintCategories/${item.complaintCategoryId}`, {
        name: item.name,
        displayOrder: item.displayOrder ?? 0,
        isActive: nextActive,
      });
      await loadCategories();
      setFlash({
        message: nextActive ? "تم تفعيل التصنيف بنجاح." : "تم إيقاف التصنيف بنجاح.",
        variant: "success",
      });
    } catch (err) {
      setFlash({
        message: err.response?.data?.message || `تعذر ${label} التصنيف. حاول مرة أخرى.`,
        variant: "danger",
      });
    }
  };

  const closeModal = () => {
    setModal(null);
    setModalCategoryId(null);
    setModalCategoryInitial(null);
  };

  const onModalSaved = ({ message, variant } = {}) => {
    loadCategories();
    setFlash({
      message: message || "تم حفظ التغييرات بنجاح",
      variant: variant || "success",
    });
  };

  const openCreate = () => {
    if (!canCreate) return;
    setModal("create");
    setModalCategoryId(null);
    setModalCategoryInitial(null);
  };

  const openEdit = (item) => {
    if (!canEdit) return;
    setModal("edit");
    setModalCategoryId(item.complaintCategoryId);
    setModalCategoryInitial(item);
  };

  return (
    <div className="users-page-minimal users-admin-page" style={{ direction: "rtl", fontFamily: "Cairo, sans-serif" }}>
      <div className="container pb-5 pt-4 px-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 pb-3 border-bottom gap-3">
          <div>
            <h3 className="fw-bold text-primary mb-1 d-flex align-items-center gap-2">
              <span className="rounded-3 bg-primary bg-opacity-10 p-2 d-inline-flex text-primary">
                <FaTags />
              </span>
              تصنيفات الشكاوي
            </h3>
            <p className="text-muted small mb-0">إدارة تصنيفات الشكاوي وترتيب عرضها</p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {canCreate && (
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center px-4 rounded-pill shadow-sm"
                onClick={openCreate}
              >
                <i className="bi bi-plus-lg ms-2" /> إضافة تصنيف
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate(PATHS.ADMIN.HOME)}
              className="btn btn-outline-secondary rounded-pill"
            >
              <FaArrowRight className="ms-1" /> رجوع
            </button>
          </div>
        </div>

        {flash?.message ? (
          <div
            className={`users-admin-alert px-3 py-2 mb-3 d-flex align-items-center justify-content-between gap-2 ${flash.variant === "danger" ? "users-admin-alert--danger" : ""}`}
            role="alert"
          >
            <span>{flash.message}</span>
            <button
              type="button"
              className={`btn btn-sm btn-link p-0 ${flash.variant === "danger" ? "text-danger" : "text-success"}`}
              onClick={() => setFlash(null)}
            >
              إغلاق
            </button>
          </div>
        ) : null}

        <div className="users-toolbar mb-3">
          <div className="users-count-chip">
            إجمالي التصنيفات
            <span>{categories.length}</span>
          </div>
          <div className="users-search-wrap">
            <div className="bg-white border rounded-4 p-2 shadow-sm">
              <div className="row g-2 align-items-center">
                <div className="col-md-8 border-end-md">
                  <div className="input-group border-0">
                    <span className="input-group-text bg-transparent border-0 text-muted">
                      <i className="bi bi-search" />
                    </span>
                    <input
                      type="text"
                      className="form-control border-0 shadow-none py-2"
                      placeholder="ابحث باسم التصنيف..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <select
                    className="form-select form-select-sm border-0 shadow-none fw-medium"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">جميع الحالات</option>
                    <option value="active">نشط فقط</option>
                    <option value="inactive">متوقف فقط</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border spinner-border-sm text-secondary" />
          </div>
        ) : (
          <div className="users-table-shell">
            <div className="table-responsive">
              <table className="table users-table align-middle mb-0">
                <thead>
                  <tr>
                    <th className="text-center col-index">#</th>
                    <th className="text-end">اسم التصنيف</th>
                    <th className="text-center">ترتيب العرض</th>
                    <th className="text-center">الحالة</th>
                    {hasAnyAction && <th className="text-center">الإجراءات</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredCategories.length === 0 ? (
                    <tr>
                      <td colSpan={hasAnyAction ? 5 : 4}>
                        <div className="empty-state">
                          <i className="bi bi-inbox fs-4 mb-2" />
                          <div>لا توجد تصنيفات مطابقة للبحث</div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredCategories.map((item, index) => (
                      <tr key={item.complaintCategoryId}>
                        <td className="text-center col-index fw-semibold text-muted">{index + 1}</td>
                        <td className="text-end">
                          <span className="user-name">{item.name}</span>
                        </td>
                        <td className="text-center">
                          <span className="role-pill">{item.displayOrder ?? 0}</span>
                        </td>
                        <td className="text-center">
                          {item.isActive ? (
                            <span className="status-pill status-active">نشط</span>
                          ) : (
                            <span className="status-pill status-inactive">متوقف</span>
                          )}
                        </td>
                        {hasAnyAction && (
                          <td className="text-center">
                            <div className="d-flex gap-2 justify-content-center flex-wrap">
                              {canEdit && (
                                <button
                                  type="button"
                                  className="btn-minimal"
                                  title="تعديل"
                                  onClick={() => openEdit(item)}
                                >
                                  <i className="bi bi-pencil" />
                                </button>
                              )}
                              {canEdit && (
                                <button
                                  type="button"
                                  className={`btn-minimal ${item.isActive ? "text-danger-hover" : "text-success"}`}
                                  title={item.isActive ? "إيقاف" : "تفعيل"}
                                  onClick={() => handleToggleStatus(item)}
                                >
                                  <i className={`bi ${item.isActive ? "bi-pause-circle" : "bi-play-circle"}`} />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <ComplaintCategoriesModals
          modal={modal}
          categoryId={modalCategoryId}
          initialCategory={modalCategoryInitial}
          defaultDisplayOrder={categories.length + 1}
          onClose={closeModal}
          onSaved={onModalSaved}
          canCreate={canCreate}
          canEdit={canEdit}
        />
      </div>
    </div>
  );
}
